import { FaBatteryFull, FaSignal, FaWifi } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

export type SmsTemplateFormData = Record<string, unknown>;

type SmsTemplatePreviewProps = {
  formData: SmsTemplateFormData;
  /** `inline` sticky column for create/edit; `sheet` for SlideOver (no sticky aside). */
  variant?: 'inline' | 'sheet';
};

const pickString = (formData: SmsTemplateFormData, keys: string[]): string => {
  for (const key of keys) {
    const value = formData[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

export const SmsTemplatePreview = ({ formData, variant = 'inline' }: SmsTemplatePreviewProps) => {
  const { t } = useTranslation('campaign');
  const { t: tCommon } = useTranslation('COMMON');
  const isSheet = variant === 'sheet';

  const bodyText = pickString(formData, ['template_text', 'template_body_text', 'text', 'body', 'message']);
  const templateName = pickString(formData, ['template_name', 'name']);
  const sender = pickString(formData, ['sender', 'sender_id', 'from']) || t('smsWorkspace.templates.smsPreviewSenderFallback');
  const senderInitial = (sender.charAt(0) || 'S').toUpperCase();
  const previewBody = bodyText || t('smsWorkspace.templates.smsPreviewPlaceholder');

  return (
    <aside className={twMerge(!isSheet && 'sticky top-6')}>
      {!isSheet ? (
        <div className='mb-3 flex items-center justify-between gap-3'>
          <div>
            <h2 className='text-sm font-semibold text-gray-900'>{t('smsWorkspace.templates.smsPreview')}</h2>
            <p className='mt-1 text-xs text-gray-500'>{t('smsWorkspace.templates.smsPreviewDescription')}</p>
          </div>
          <span className='shrink-0 rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700'>
            {t('smsWorkspace.templates.smsPreviewBadge')}
          </span>
        </div>
      ) : null}

      {/* Phone shell: black physical bezel around white screen */}
      <div className='mx-auto w-full max-w-[320px] rounded-[2.5rem] bg-black p-[10px] shadow-xl'>
        <div className='relative overflow-hidden rounded-[1.75rem] bg-white'>
          {/* Speaker slit */}
          <div className='absolute left-1/2 top-2.5 z-10 h-1 w-16 -translate-x-1/2 rounded-full bg-black/80' />

          <div className='flex min-h-[500px] flex-col bg-white pt-4'>
            {/* Status bar */}
            <div className='flex h-7 items-center justify-between bg-white px-5 text-[11px] text-gray-900'>
              <span className='font-medium'>12:45</span>
              <div className='flex items-center gap-1.5 text-gray-900'>
                <FaSignal className='text-[10px]' />
                <FaWifi className='text-[11px]' />
                <FaBatteryFull className='text-[12px]' />
              </div>
            </div>

            {/* Sender */}
            <div className='flex flex-col items-center gap-1.5 border-b border-gray-100 px-4 pb-4 pt-3'>
              <div className='flex h-14 w-14 items-center justify-center rounded-full bg-slate-300 text-xl font-semibold text-white'>
                {senderInitial}
              </div>
              <div className='text-xs font-semibold uppercase tracking-[0.08em] text-slate-700'>{sender}</div>
            </div>

            {/* Messages */}
            <div className='flex flex-1 flex-col bg-white px-4 py-5'>
              <div className='mb-3 text-center text-[11px] text-gray-400'>
                {tCommon('todayText')} 12:45
              </div>

              <div className='flex justify-start'>
                <div className='max-w-[85%] rounded-2xl rounded-tl-sm bg-slate-100 px-3.5 py-2.5'>
                  <p className={`whitespace-pre-wrap text-[13px] leading-5 ${bodyText ? 'text-gray-900' : 'text-gray-400'}`}>
                    {previewBody}
                  </p>
                </div>
              </div>

              <p className='mt-2 text-[11px] text-gray-400'>{t('smsWorkspace.templates.smsPreviewDelivered')}</p>
            </div>

            {/* Home indicator */}
            <div className='flex justify-center bg-white pb-2.5 pt-1'>
              <div className='h-1 w-28 rounded-full bg-black/90' />
            </div>
          </div>
        </div>
      </div>

      {!isSheet && templateName ? (
        <div className='mt-4 rounded-xl border border-gray-200 bg-white p-3 text-xs'>
          <div className='text-gray-400'>{t('smsWorkspace.templates.tableName')}</div>
          <div className='mt-1 font-medium text-gray-800'>{templateName}</div>
        </div>
      ) : null}
    </aside>
  );
};
